import { useState } from 'react';
import { AiOutlinePercentage } from 'react-icons/ai';
import { FiTrash, FiEdit3 } from 'react-icons/fi';
import { toastSuccess } from '../utils/toasts';
import FilterTable, { BaseData } from './Table/Table';
import { EventAudienceDiscountDTO } from '../models/event';
import CustomModal from './atoms/CustomModal';
import { useDeleteAudienceDiscountMutation } from '../store';
import Button from './atoms/Button';
import useGetParams from '../hooks/useGetParams';
import CreateEditAudienceDiscount from './CreateEditAudienceDiscount';
import EmptyTable from './Table/EmptyTable';

interface AudienceDiscountsTableProps {
  data: EventAudienceDiscountDTO[];
}

interface TableDiscounts extends BaseData {
  name: string | JSX.Element;
  active: string | number | JSX.Element;
  percentage: string | JSX.Element;
  requirements: string | JSX.Element;
}

const AudienceDiscountsTable: React.FC<AudienceDiscountsTableProps> = ({ data }) => {
  const { businessId, eventId } = useGetParams();
  const [showModal, setShowModal] = useState(false);
  const [modalBody, setModalBody] = useState<JSX.Element | null>(null);
  const [deleteAudienceDiscount, { isLoading: isDeletingDiscount }] = useDeleteAudienceDiscountMutation();

  const handleCreate = () => {
    setShowModal(true);
    setModalBody(
      <div className='flex flex-col gap-4 max-w-[600px]'>
        <p className='text-2xl font-medium'>Agregar descuento</p>
        <CreateEditAudienceDiscount onSuccess={() => { setShowModal(false); }} />
      </div>,
    );
  };

  const handleEdit = (row: TableDiscounts) => {
    const originalDiscount = data.find((d) => d.id === row.id);
    if (originalDiscount) {
      setShowModal(true);
      setModalBody(
        <div className='flex flex-col gap-4 max-w-[600px]'>
          <p className='text-2xl font-medium'>Editar descuento</p>
          <CreateEditAudienceDiscount discount={originalDiscount} onSuccess={() => {
            setShowModal(false);
          }} />
        </div>,
      );
    }
  };

  const handleDeleteRow = async (row: TableDiscounts) => {
    try {
      await deleteAudienceDiscount({
        businessId,
        eventId,
        discountId: row.id,
      });
      toastSuccess('Descuento eliminado correctamente');
      setShowModal(false);
    } catch (error) {
      console.error(error);
    }
  };

  const handleDelete = (row: TableDiscounts) => {
    setShowModal(true);
    setModalBody(<div className='flex flex-col gap-4 max-w-[600px]'>
      <p className='text-2xl font-medium'>Eliminar descuento</p>
      <p>¿Estás seguro de querer eliminar este descuento?</p>
      <div className='flex flex-row gap-2'>
        <Button variant="secondary" onClick={() => setShowModal(false)}>Cancelar</Button>
        <Button loading={isDeletingDiscount} onClick={() => handleDeleteRow(row)}>Eliminar</Button>
      </div>
    </div>);
  };

  const headCells = [
    {
      id: 'name',
      label: 'Nombre',
    },
    {
      id: 'active',
      label: 'Visible para el público',
    },
    {
      id: 'percentage',
      label: 'Porcentaje',
    },
    {
      id: 'requirements',
      label: 'Requisitos',
    },
  ];

  const rows: TableDiscounts[] = data.map((discounts) => ({
    id: discounts.id,
    name: <div className='whitespace-nowrap'>{discounts.name}</div>,
    active: discounts.active ? (
      <div className='tag-primary'>
        Habilitado
      </div>
    ) : (
      <div className='tag-warning'>
        No habilitado
      </div>
    ),
    percentage: `${discounts.percentage * 100}%`,
    requirements: <div className='whitespace-nowrap'>{discounts.requirements}</div>,
  }));

  const actions = (row: TableDiscounts) => <div className='flex flex-row gap-2 items-center'>
    <FiEdit3 onClick={() => handleEdit(row)} className='cursor-pointer w-5 h-5' />
    <FiTrash onClick={() => handleDelete(row)} className='cursor-pointer w-5 h-5' />
  </div>;

  const customButton = <Button onClick={handleCreate} variant="secondary">
    <div className='flex flex-row gap-2 items-center'>
      <AiOutlinePercentage className='w-6 h-6' />
      <p className='whitespace-nowrap hidden md:block'>Agregar descuento</p>
    </div>
  </Button>;

  return (
    <div className='glass-surface'>
      <h1 className='text-2xl font-medium mb-2'>Descuentos</h1>
      {data.length > 0 ? <FilterTable
        rows={rows}
        headCells={headCells}
        pagination={{
          pageSize: 10,
          enabled: true,
        }}
        tableId="discount-table"
        customButtons={[customButton]}
        actions={actions}
      />
        : <EmptyTable
          title="No hay descuentos"
          info="Agrega un descuento para que los clientes puedan utilizarlo en la plataforma"
          icon={AiOutlinePercentage}
          iconColor="text-primary-500"
          button={[
            {
              label: 'Agregar descuento',
              onClick: handleCreate,
              variant: 'secondary',
              icon: AiOutlinePercentage,
            },
          ]}
        />}
      <CustomModal
        isOpen={showModal}
        onRequestClose={() => setShowModal(false)}
        hideCancelButton
      >
        {modalBody}
      </CustomModal>
    </div>
  );
};

export default AudienceDiscountsTable;
